import {
  isBlendedCareProvider,
  isDAProviderOfferingMeds,
  isDAProviderOfferingTherapy,
  isICASCounsellor,
} from '@lyrahealth-inc/shared-app-logic/src/features/providers/utils'
import type { ProviderUser } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

import { hasRole, isEngageDAProvider } from './utils'
import { BEND_ROLES } from '../constants/appConstants'

export type ProviderPortalExperience = 'lcx' | 'da_engage' | 'da_legacy' | 'unknown'

export type PendoProviderVisitor = {
  id: string
  email?: string
  role: string
  roles: string[]
  country: string
  onsite: boolean
  environment: string
  provider_portal_experience: ProviderPortalExperience
  has_lcx_program: boolean
  has_da_therapy: boolean
  has_icas_therapy: boolean
  has_da_meds: boolean
  is_bend_provider: boolean
  program_taxonomy_status: 'available' | 'missing'
  timestamp_approved?: string
}

type BuildPendoProviderVisitorArgs = {
  user: ProviderUser
  environment: string
  isDAProviderMigrated?: boolean
}

type ResolvePendoDAMigrationEvaluationArgs = {
  userId: string
  requiresDAMigrationEvaluation: boolean
  ldIdentification?: {
    userId: string
    status: 'ready' | 'failed'
  }
  isDAProviderMigrated: boolean
}

const ISO_8601_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/

export const normalizeTimestampApproved = (timestampApproved?: string | null): string | undefined => {
  const normalized = timestampApproved?.trim()
  const match = normalized && ISO_8601_DATE_TIME.exec(normalized)
  if (!match) {
    return undefined
  }

  const [, yearString, monthString, dayString, hourString, minuteString, secondString] = match
  const [year, month, day, hour, minute, second] = [
    yearString,
    monthString,
    dayString,
    hourString,
    minuteString,
    secondString,
  ].map(Number)
  const calendarDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second))
  // Provider Portal API currently emits timezone-less timestamps as UTC. Preserve
  // explicit offsets, and make that API assumption explicit for timezone-less values.
  const timestampWithTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(normalized) ? normalized : `${normalized}Z`
  const timestamp = Date.parse(timestampWithTimezone)
  const hasValidCalendarDate =
    calendarDate.getUTCFullYear() === year &&
    calendarDate.getUTCMonth() === month - 1 &&
    calendarDate.getUTCDate() === day &&
    calendarDate.getUTCHours() === hour &&
    calendarDate.getUTCMinutes() === minute &&
    calendarDate.getUTCSeconds() === second
  if (!hasValidCalendarDate || !Number.isFinite(timestamp) || timestamp <= 0) {
    return undefined
  }

  return new Date(timestamp).toISOString()
}

export const hydratePendoProviderMetadata = (
  provider: ProviderUser,
  providerDetails?: Partial<ProviderUser>,
): ProviderUser => ({
  // The result becomes the app-wide selected provider, so preserve summary-list fields that drive
  // application behavior. In particular, roles must not come from detail: Provider.to_dict()
  // returns [] whenever there is no user profile, which would blank the provider's navigation.
  ...provider,
  program_taxonomies: providerDetails?.program_taxonomies ?? provider.program_taxonomies,
  timestamp_approved: providerDetails?.timestamp_approved ?? provider.timestamp_approved,
  // Detail-only values needed for accurate Pendo metadata stay isolated from state.user fields
  // consumed by navigation, ECDC visibility, and payment workflows.
  pendo_metadata: {
    email: providerDetails?.email ?? provider.pendo_metadata?.email ?? provider.email,
    country: providerDetails?.country ?? provider.pendo_metadata?.country ?? provider.country,
    onsite: providerDetails?.onsite ?? provider.pendo_metadata?.onsite ?? provider.onsite,
  },
})

export const resolvePendoDAMigrationEvaluation = ({
  userId,
  requiresDAMigrationEvaluation,
  ldIdentification,
  isDAProviderMigrated,
}: ResolvePendoDAMigrationEvaluationArgs): { isReady: boolean; migrationValue?: boolean } => {
  const hasCurrentLDIdentification = ldIdentification?.userId === userId

  return {
    isReady: !requiresDAMigrationEvaluation || hasCurrentLDIdentification,
    // Only surface the flag to providers whose classification depends on it. Otherwise the value
    // arriving after Pendo is already initialized churns the visitor memo and fires an
    // updateOptions call whose payload is identical to what was already sent.
    migrationValue:
      requiresDAMigrationEvaluation && hasCurrentLDIdentification && ldIdentification?.status === 'ready'
        ? isDAProviderMigrated
        : undefined,
  }
}

export const buildPendoProviderVisitor = ({
  user,
  environment,
  isDAProviderMigrated,
}: BuildPendoProviderVisitorArgs): PendoProviderVisitor => {
  const pendoMetadata = user.pendo_metadata
  const roles = user.roles ?? []
  const programTaxonomies = user.program_taxonomies ?? []
  const hasLcxProgram = isBlendedCareProvider({ programTaxonomy: programTaxonomies })
  const hasDATherapy = isDAProviderOfferingTherapy({ programTaxonomy: programTaxonomies })
  const hasICASTherapy = isICASCounsellor({ programTaxonomy: programTaxonomies })
  const hasDAMeds = isDAProviderOfferingMeds({ programTaxonomy: programTaxonomies })
  const isBendProvider = hasRole(roles, BEND_ROLES)

  let providerPortalExperience: ProviderPortalExperience = 'unknown'
  if (isBendProvider) {
    providerPortalExperience = 'da_engage'
  } else if (isEngageDAProvider(roles)) {
    providerPortalExperience =
      isDAProviderMigrated === undefined ? 'unknown' : isDAProviderMigrated ? 'da_engage' : 'da_legacy'
  } else if (hasLcxProgram) {
    providerPortalExperience = 'lcx'
  } else if (hasDATherapy || hasDAMeds) {
    providerPortalExperience = 'da_legacy'
  }

  const timestampApproved = normalizeTimestampApproved(user.timestamp_approved)
  const email = pendoMetadata?.email ?? user.email
  const country = pendoMetadata?.country ?? user.country
  const onsite = (pendoMetadata?.onsite ?? user.onsite) === true

  return {
    id: user.id,
    ...(email ? { email } : {}),
    role: roles.join(', '),
    roles,
    country: country ?? 'unknown',
    onsite,
    environment,
    provider_portal_experience: providerPortalExperience,
    has_lcx_program: hasLcxProgram,
    has_da_therapy: hasDATherapy,
    has_icas_therapy: hasICASTherapy,
    has_da_meds: hasDAMeds,
    is_bend_provider: isBendProvider,
    program_taxonomy_status: programTaxonomies.length > 0 ? 'available' : 'missing',
    ...(timestampApproved ? { timestamp_approved: timestampApproved } : {}),
  }
}
